/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import AssetsObjectTypeIcon from '@atlaskit/icon-lab/core/assets-object-type';
import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import Image from '@atlaskit/image';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import type { OptionType } from '@atlaskit/select';

import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as assetsTypeFilter } from '../../../../../controllers/store/search/filters/assets-type';

import { i18n } from './messages';

const styles = cssMap({
	imageContainer: {
		width: '16px', //  width throws a type error when using tokens
		height: '16px',
	},
});

const AssetsTypeFilter = ({
	isFilterInDropdown,
	onClearOptions,
}: FilterProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: assetsTypeFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const formatOptionLabel = (data: OptionType) => {
		return (
			<Inline alignBlock="center" space="space.100">
				<Flex>
					<Image src={data.avatarUrl} alt={data.label} css={styles.imageContainer} />
				</Flex>
				<Stack space="space.025">
					<Text>{data.label}</Text>
					<Inline alignBlock="center" space="space.100">
						<AssetsSchemaIcon label="" size="small" color="var(--ds-text-subtlest)" />
						<Text color="color.text.subtlest">{data.metadata.schemaName}</Text>
					</Inline>
				</Stack>
			</Inline>
		);
	};

	const groupedOptions = [
		{
			label: formatMessage(i18n.assetsFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.assetsFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	return (
		<SelectFilter
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={formatMessage(i18n.assetsType)}
			placeholder={formatMessage(i18n.assetsFilterAssetsTypePlaceholder)}
			filterOrControlId={'assetsType'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: assetsTypeFilter.id });
			}}
			iconBefore={
				<AssetsObjectTypeIcon label={formatMessage(i18n.assetsIconLabel)} size="medium" />
			}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};

export default AssetsTypeFilter;
